import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; 
import { CartContext } from '../../context/CartContext'; 
import './Header.css'; 

// 🚨 VARIABLE DINÁMICA: Detecta si estás en tu PC o en Hostinger
const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://neoclic.kern-studio.com';

// 📱 NÚMERO DE WHATSAPP DE TU TIENDA (Incluye código de país sin +)
const WHATSAPP_NUMBER = '573001234567'; // Reemplaza con tu número real

const Header = () => {
  const { user } = useAuth(); 
  const navigate = useNavigate(); 
  const { cart, setIsCartOpen } = useContext(CartContext); 
  const totalItems = cart.reduce((total, item) => total + item.cantidad, 0);

  // ==========================================
  // ESTADOS DEL BUSCADOR EN VIVO
  // ==========================================
  const [searchTerm, setSearchTerm] = useState(''); 
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(`${API_BASE}/api/productos/buscar/en-vivo?q=${encodeURIComponent(searchTerm)}`);
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error("Error buscando productos:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchEnter = (e) => {
    if (e.key === 'Enter') {
      setShowDropdown(false);
      if (searchTerm.trim() !== '') {
        navigate(`/catalogo?search=${encodeURIComponent(searchTerm.trim())}`);
      } else {
        navigate('/catalogo');
      }
    }
  };

  const handleProductClick = (id) => {
    setShowDropdown(false);
    setSearchTerm('');
    navigate(`/producto/${id}`);
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate(`/catalogo?search=${encodeURIComponent(searchTerm.trim())}`);
  };

  // ==========================================
  // ESTADOS Y LÓGICA DE NOTIFICACIONES (API REAL)
  // ==========================================
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);

  useEffect(() => {
    if (user && user.id) {
      const fetchNotificaciones = async () => {
        try {
          const response = await fetch(`${API_BASE}/api/notificaciones/${user.id}`);
          if (response.ok) {
            const data = await response.json();
            setNotificaciones(data);
          }
        } catch (error) {
          console.error("Error al cargar notificaciones:", error);
        }
      };
      fetchNotificaciones();
    }
  }, [user]);

  const unreadCount = notificaciones.filter(n => !n.leido).length;

  const marcarComoLeidas = async () => {
    if (unreadCount === 0) return; 
    setNotificaciones(notificaciones.map(n => ({ ...n, leido: true })));
    try {
      await fetch(`${API_BASE}/api/notificaciones/${user.id}/leer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error("Error al marcar como leídas:", error);
    }
  };

  // Formateador de moneda colombiana
  const formatearPrecio = (precio) => {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(precio);
  };

  return (
    <header className="header-container">
      
      {/* 1. IZQUIERDA: Logo */}
      <div className="header-logo-section">
        <Link to="/">
          <img 
            src="/uploads/logo.png" 
            alt="NeoClic" 
            className="header-logo"
          />
        </Link>
      </div>

      {/* 2. CENTRO: Buscador en Vivo */}
      <div className="header-search-section" ref={searchRef}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input 
            type="text" 
            placeholder="BUSCAR..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            onKeyDown={handleSearchEnter} 
            onFocus={() => { if (searchTerm.trim() !== '') setShowDropdown(true); }}
            className="search-input"
          />

          {showDropdown && searchTerm.trim() !== '' && (
            <div className="live-search-dropdown">
              {isSearching ? (
                <div className="live-search-loading">Buscando...</div>
              ) : searchResults.length > 0 ? (
                <>
                  <div className="live-search-results">
                    {searchResults.map((item) => (
                      <div 
                        key={item.id} 
                        className="live-search-card"
                        onClick={() => handleProductClick(item.id)}
                      >
                        <div className="live-search-img-container">
                          <img 
                            src={item.ruta_imagen ? `${API_BASE}${item.ruta_imagen}` : '/placeholder.png'} 
                            alt={item.titulo} 
                          />
                        </div>
                        <div className="live-search-info">
                          <h4 className="live-search-title">{item.titulo}</h4>
                          <span className="live-search-stock">
                            Unidades disponibles: {item.existencias}
                          </span>
                        </div>
                        <div className="live-search-price">
                          {formatearPrecio(item.precio_base)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="live-search-footer" onClick={handleViewAll}>
                    Ver todos los resultados para "{searchTerm}"
                  </div>
                </>
              ) : (
                <div className="live-search-empty">
                  No se encontraron productos con estos filtros.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 3. DERECHA: WhatsApp, Carrito, Notificaciones y Perfil */}
      <div className="header-actions-section">
        
        {/* 🟢 NUEVO: BOTÓN DE WHATSAPP */}
        <a 
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('¡Hola NeoClic! Quisiera recibir asesoría con un producto.')}`}
          target="_blank" 
          rel="noopener noreferrer"
          className="whatsapp-icon-wrapper"
          title="Contactar por WhatsApp"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
          </svg>
        </a>

        {/* ICONO DEL CARRITO */}
        <div 
          className="cart-icon-wrapper"
          onClick={() => setIsCartOpen(true)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          
          {totalItems > 0 && (
            <span className="cart-badge">
              {totalItems}
            </span>
          )}
        </div>

        {/* ZONA DE AUTENTICACIÓN */}
        {user ? (
          <>
            {/* === SISTEMA DE NOTIFICACIONES === */}
            <div className="notification-wrapper">
              <div 
                className="notification-icon"
                onClick={() => {
                  setIsNotifOpen(!isNotifOpen);
                  if (!isNotifOpen) marcarComoLeidas();
                }}
                style={{ cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center' }}
              >
                <span style={{ fontSize: '20px' }}>🔔</span>
                {unreadCount > 0 && (
                  <span className="notification-badge" style={{
                    position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#e11d48', color: 'white', fontSize: '10px', fontWeight: 'bold', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>

              {/* PANEL DESPLEGABLE */}
              {isNotifOpen && (
                <div className="notifications-dropdown">
                  <div className="notif-header">
                    <h3>Notificaciones</h3>
                    <button className="close-notif-btn" onClick={() => setIsNotifOpen(false)}>✖</button>
                  </div>
                  
                  <div className="notif-body">
                    {notificaciones.length === 0 ? (
                      <p className="no-notifs">No tienes notificaciones nuevas.</p>
                    ) : (
                      notificaciones.map(notif => (
                        <div key={notif.id} className={`notif-item ${!notif.leido ? 'unread' : ''}`}>
                          <div className="notif-icon-emoji">
                            {notif.tipo === 'pedido' ? '📦' : '🏷️'}
                          </div>
                          <div className="notif-content">
                            <h4>{notif.titulo}</h4>
                            <p>{notif.mensaje}</p>
                            <span className="notif-time">
                              {new Date(notif.fecha_creacion).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <Link to="/perfil" className="profile-link">
              <div className="profile-avatar">
                {user.foto ? (
                  <img src={user.foto} alt="Perfil" className="profile-img" />
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                )}
              </div>
            </Link>
          </>
        ) : (
          <>
            <Link to="/login" className="login-link">INICIAR SESIÓN</Link>
            <Link to="/register" className="register-link">REGISTRARSE</Link>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;